// PrintOptionsDialog - MODULE
// ***********************************************************************************************************************
import { Box, Grid, TextField } from "@mui/material";
import * as React from "react";
import { TreeCheckBoxDropdownItems } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown/TreeCheckboxDropdownItems";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ApplicationPermissionType, ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { ISavedPrint, SavedPrint } from "../../../models/common/savedPrint";
import * as printService from "../../../models/services/printService";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogButtons } from "../Dialog/DialogButtons";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../Grid/GridRow";

//PrintOptionsDialog
export interface IPrintOptionsDialogProp {
    classes?: string;
    print: ISavedPrint;
    printableFields: IIdTitleNode[];
    applicationPermissionType: ApplicationPermissionType;
    reservedNames: string[];
    onPrintSaved: (print: ISavedPrint) => void;
    onPrintRemoved: (printId: string) => void;
    onCancel: () => void;
}

export interface IPrintOptionsDialogState {
    name: string;
    fields: string[];
}

export class PrintOptionsDialog extends React.Component<IPrintOptionsDialogProp, IPrintOptionsDialogState> {
    private orgStateHash: string = "";

    getStateFromProps = (props: IPrintOptionsDialogProp): IPrintOptionsDialogState => {
        return {
            name: props.print.title,
            fields: props.print.fields.slice(0)
        };
    };

    constructor(props: IPrintOptionsDialogProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getSaveDataFromState(props, this.state).hash;
    }

    componentDidUpdate(prevProps: IPrintOptionsDialogProp, prevState: IPrintOptionsDialogState): void {
        const props = this.props;
        if (!prevProps || prevProps.print.getHash() === props.print.getHash()) return;
        const state = this.getStateFromProps(props);
        this.setState(state);
        this.orgStateHash = this.getSaveDataFromState(props, state).hash;
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "name") {
            this.setState({ name: value });
        }
    };

    handleSetSelectedField = (selected: boolean, fieldId: string) => {
        if (!fieldId) return;
        const fields = this.state.fields.filter(i => i !== fieldId);
        if (selected) {
            fields.push(fieldId);
        }
        this.setState({
            fields
        });
    };

    getSaveDataFromState = (props: IPrintOptionsDialogProp, state: IPrintOptionsDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("fields", state.fields);
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.name) {
                AppUtils.showErrorMessage(Translations.NameMustBeDefined);
                return resolve(false);
            }
            if (props.reservedNames.find(i => i.equalIgnoreCase(state.name))) {
                AppUtils.showErrorMessage(Translations.NameIsAlreadyInUse);
                return resolve(false);
            }
            return resolve(true);
        });
    };

    handleSave = async () => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return false;
        const print = new SavedPrint(props.print);
        if (!props.print.defaultPrint) {
            print.id = state.name;
            print.title = state.name;
        }
        print.fields = state.fields.slice(0);
        const saveData = new SaveDataObj();
        saveData.append("applicationPermissionType", props.applicationPermissionType);
        saveData.append("orgId", props.print.id);
        saveData.append("item", print);
        //Call server
        const result = await AppUtils.callService(() => printService.savePrint(saveData.obj));
        if (!result) return null;
        props.onPrintSaved(print);
    };

    handleRemove = async () => {
        const props = this.props;
        if (props.print.isNew()) return;
        const confirmationResult = await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemovePrintParameter, props.print.title));
        if (confirmationResult !== ConfirmationDialogResult.Yes) return;
        const saveData = new SaveDataObj();
        saveData.append("applicationPermissionType", props.applicationPermissionType);
        saveData.append("id", props.print.id);
        const result = await AppUtils.callService(() => printService.removePrint(saveData.obj), false);
        if (!result) return;
        props.onPrintRemoved(props.print.id);
    };

    handleCancel = async (checkChanges: boolean) => {
        AppUtils.cancelEditDialog(checkChanges, this.getSaveDataFromState(this.props, this.state).hash, this.orgStateHash, this.handleSave, this.props.onCancel, Translations.YouHaveUnapprovedChangesDoYouWantApproveChanges);
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <Dialog
                id="print-options-dialog"
                title={props.print.title}
                body={
                    <>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.Name} required margin={0}>
                                    <TextField
                                        type="text"
                                        name="name"
                                        title={Translations.Name}
                                        inputProps={{ maxLength: 50 }}
                                        value={state.name}
                                        required={true}
                                        onChange={this.handleChange}
                                        disabled={props.print.defaultPrint}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.FieldsToBePrinted}>
                                    <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
                                        <TreeCheckBoxDropdownItems
                                            selectedIds={state.fields}
                                            toggleAllId={""}
                                            values={props.printableFields}
                                            onChange={(ids: string[]) => this.setState({ fields: ids })}
                                        />
                                    </Box>
                                </FormField>
                            </Grid>
                        </GridRow>
                    </>
                }
                actions={
                    <DialogButtons
                        isNew={props.print.isNew()}
                        onCancel={() => this.handleCancel(false)}
                        onRemove={!props.print.isNew() && !props.print.defaultPrint ? this.handleRemove : null}
                        onSave={this.handleSave}
                    />
                }
                onClose={() => this.handleCancel(true)}/>
        );
    }
}